import { useState, useEffect, useMemo } from "react";
import { Modal, ModalHeader, ModalBody } from "reactstrap";

import ReactTable from "components/ReactTable/ReactTable";
import useFetch from "Hooks/useFetch";
import useAlertContext from "Hooks/useAlertContext";
import styles from "Pages/Integration/Components/ArgumentPicker/ArgumentPicker.module.css";
interface IArgumentData {
    [key: string]: string;
}
interface IProps {
    argument: IArgument | undefined;
    argumentData: IArgumentData;
    modalOpen: boolean;
    onSetArgumentData: (argument: any) => void;
    toggle: (argument: IArgument | undefined) => void;
}

/** This is the modal that calls search bubble methods via the provider service,
 * displays the results in a table for the user to pick from, and then sets the
 * integration argument input to the selected value. This is always the first
 * column value by convention.
 * argument contains all data from integration_argument record.
 * argumentData contains the values of the argument inputs, in an object with
 * keys of 0, 1, 2, etc. corresponding to the argument number and values of the
 * string input value.
 */
export default function ArgumentPicker({
    argument,
    argumentData,
    modalOpen,
    onSetArgumentData,
    toggle,
}: IProps) {
    const [integrationArgumentOptions, setIntegrationArgumentOptions] =
        useState<IArgumentData[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const { get } = useFetch();
    const { newAlert } = useAlertContext();

    // Fetch integration arguments on modal open
    useEffect(() => {
        if (!argument) return;

        let ignore = false;

        getIntegrationArgumentOptions();

        async function getIntegrationArgumentOptions() {
            setIsLoading(true);
            if (!modalOpen || ignore) return;

            let url = `/api/integrations/arguments/${argument!.data_source}`;

            /** Add arguments from previous input(s) if any to the url */
            const args = Object.entries(argumentData)
                .sort(
                    ([prevArgumentNumber], [currentArgumentNumber]) =>
                        parseInt(prevArgumentNumber) -
                        parseInt(currentArgumentNumber)
                )
                .slice(0, argument?.argument_number)
                .reduce((previousUrl, [, currentArgumentValue]) => {
                    return currentArgumentValue !== ""
                        ? previousUrl + `/${currentArgumentValue}`
                        : previousUrl;
                }, "");

            url += args;

            console.log(
                `Requesting argument data for ArgumentPicker modal from ${url}...\n`
            );

            const response = await get(url);

            let data: any = [];
            try {
                data = (await response.json()) || response;
            } catch (e) {
                data = response;
            }
            console.log("Argument picker options request: ", data);

            if (response.ok) {
                setIntegrationArgumentOptions(data);
            } else {
                toggle(undefined);
                newAlert({
                    message: "Error - unable to retrieve integration arguments",
                    color: "danger",
                });
            }
            setIsLoading(false);
        }

        return () => {
            ignore = true;
        };
    }, [argument, argumentData, get, modalOpen, newAlert, toggle]);

    // Clear modal table content when the modal is closed
    useEffect(() => {
        if (!modalOpen) setIntegrationArgumentOptions([]);
    }, [modalOpen]);

    /** Sets the input value to what was clicked in the argument picker modal */
    function selectRow(row: any) {
        if (!argument) return;

        // Clear subsequent inputs if any, in case user changes previous input
        const newData: IArgumentData = {};
        for (const i of Object.keys(argumentData)) {
            if (parseInt(i) <= argument.argument_number) continue;
            newData[i] = "";
        }
        onSetArgumentData({
            ...newData,
            [argument.argument_number]: Object.values(row.values)[0],
        });
        toggle(argument);
    }

    const columns = useMemo(() => {
        const keys = Object.keys(integrationArgumentOptions[0] || {});
        return keys.map((key) => ({
            Header: key,
            accessor: key,
        }));
    }, [integrationArgumentOptions]);

    const tableProps = {
        data: integrationArgumentOptions,
        columns,
        isLoading,
        rowClickCallback: selectRow,
        showExportCsv: false,
    };

    return (
        <Modal isOpen={modalOpen} toggle={() => toggle(argument)}>
            <ModalHeader toggle={() => toggle(argument)}>
                Choose a value for {argument && argument.name}
            </ModalHeader>
            <ModalBody className={styles.modalBody}>
                <ReactTable {...tableProps} />
            </ModalBody>
        </Modal>
    );
}
